<script lang="ts">
    import {
        dictionary,
        swapDictionaryLanguages,
    } from "@app/services/currentDictionary.service";
    import {
        SEARCH__emptyState__Start_typing,
        SEARCH__searchBoxPlaceholder__translate,
        SEARCH__searchBox_start_typing,
    } from "@app/services/i18n.messages";
    import { getMessage } from "@app/services/i18n.service";
    import { generatePhraseUrlForCurrentDictionary } from "@app/services/url-builder.service";
    import { debounce } from "@app/utils/debounce";

    import { showChangeLanguageWidget } from "../../Dictionary/ChangeDictionary.store";
    import MobileScreen from "../../MobileScreen.svelte";
    import Keyboard from "../Keyboard.svelte";
    import SimilarPhrasesList from "../SimilarPhrasesList.svelte";

    import { keyboardActive, searchPhrase } from "../Search.store";

    import { addFloatingMessage } from "@app/components/FloatingMessages/FloatingMessages.controller";
    import {
        deactivateSearchWidget,
        searchWithWordlistWidgetActive,
        setSearchPhrase,
        similarPhrases,
        toggleKeyboard,
    } from "../Search.store";
    import FullScreenSearchAutomaticTranslations from "./FullScreenSearchAutomaticTranslations.svelte";
    import LazyAppearingComponent from "@app/components/LazyAppearingComponent.svelte";

    let searchInput: HTMLTextAreaElement;

    function dictionarySearchInputChange(event: Event) {
        const value = (event.target as HTMLTextAreaElement).value;
        setSearchPhrase(value);
    }
    let isActive = $searchWithWordlistWidgetActive;

    //activateSearchWidget();
    function focusSearchInput(input: HTMLElement) {
        input.focus({ preventScroll: true });
    }

    function deactivateSearchWidgetLocal(event: Event) {
        event.stopPropagation();
        event.preventDefault();
        setTimeout(deactivateSearchWidget, 50);
    }

    function initSearchInput(input: HTMLTextAreaElement) {
        const mainSearchInput = document.getElementById(
            "dictionary-search",
        ) as HTMLTextAreaElement;
        if (mainSearchInput && mainSearchInput.value !== input.value) {
            input.value = mainSearchInput.value;
            setSearchPhrase(mainSearchInput.value);
        }
        focusSearchInput(input);
    }

    function clearInput() {
        searchInput.value = "";
        setSearchPhrase("");
        focusSearchInput(searchInput);
    }

    function onSubmit(event: Event) {
        event.preventDefault();
        event.stopPropagation();
        if (searchInput.value.trim().length == 0) {
            addFloatingMessage({
                body: "",
                title: getMessage(SEARCH__searchBox_start_typing),
                type: "info",
                actions: [],
                key: "enter-search-phrase",
            });
            if (searchInput) {
                searchInput.focus();
            }
            return;
        }
        window.location.href = generatePhraseUrlForCurrentDictionary(
            searchInput.value.trim(),
        );
    }

    function handleKeydownOnMainInput(event: KeyboardEvent) {
        if (event.key === "Enter") {
            onSubmit(event);
        }
    }

    dictionary.subscribe(
        (newDict) => searchInput && focusSearchInput(searchInput),
    );
    let navbarElement: HTMLElement;
    searchWithWordlistWidgetActive.subscribe((val) => {
        isActive = val;
        setTimeout(() => {
            if (isActive && navbarElement) {
                navbarElement.scrollIntoView();
            }
        }, 1);
    });
</script>

<LazyAppearingComponent
    show={isActive}
    class="fixed top-0 left-0 right-0 bg-white transition-all duration-300 ease-in-out w-100dvw h-100dvh z-60 text-gray-900"
    style="width: 100dvw; height: 100dvh"
    hiddenClass="dir-aware-left-100p"
>
    <MobileScreen onBackClick={deactivateSearchWidget}>
        <div class="w-full h-full" slot="navbar" bind:this={navbarElement}>
            <p
                class="flex items-center justify-center whitespace-nowrap px-1 w-full h-full"
            >
                <button
                    class="changeLanguageButton px-3 h-full font-medium leading-5 text-gray-700 focus:outline-none transition duration-300 ease-in-out w-1/2 flex items-center justify-center"
                    on:click={() => {
                        showChangeLanguageWidget("source");
                    }}
                    type="button"
                >
                    <span class="truncate">{$dictionary.l1.name}</span>
                    <span class="icon-arrow-down" />
                </button>
                <button class="px-1" on:click={swapDictionaryLanguages}
                    ><span
                        class="h-full icon-swap-horizontal text-xl text-gray-700"
                    /></button
                >
                <button
                    class="changeLanguageButton px-3 h-full font-medium leading-5 text-gray-700 focus:outline-none transition duration-300 ease-in-out w-1/2 flex items-center justify-center"
                    on:click={() => showChangeLanguageWidget("target")}
                    type="button"
                >
                    <span class="truncate">{$dictionary.l2.name}</span>
                    <span class="icon-arrow-down" />
                </button>
                <button
                    class="py-1 px-2 md:px-1 text-gray-500 focus:outline-none keyboard-activator"
                    class:icon-keyboard={!$keyboardActive}
                    class:icon-keyboard-close={$keyboardActive}
                    class:hidden={!$dictionary.l1.hasSpecialChars}
                    data-element="keyboard"
                    type="button"
                    aria-label="keyboard"
                    on:click={toggleKeyboard}
                />
                <span class="h-8 w-8 block" class:hidden={$dictionary.l1.hasSpecialChars}>
<!-- keyboard icon placeholder -->
                </span>
            </p>
        </div>
        <div class="w-full bg-white">
            <div class="bg-slate-200 w-full border-t border-b border-slate-300">
                <form
                    action={`/${$dictionary.l1.code}/${$dictionary.l2.code}`}
                    id="dictionary-search-form"
                    on:submit={onSubmit}
                    class="bg-slate-200 flex"
                >
                    <textarea
                        title="Search"
                        class="w-full text-base text-gray-800 focus:outline-none bg-slate-200 px-3 py-2"
                        style="min-height: 5rem"
                        class:dir-aware-pr-6={!$dictionary.l1.hasSpecialChars}
                        class:dir-aware-pr-16={$dictionary.l1.hasSpecialChars}
                        autofocus={true}
                        name="q"
                        spellcheck="false"
                        autocorrect="off"
                        autocomplete="off"
                        autocapitalize="none"
                        id="mobile-dictionary-search"
                        placeholder={getMessage(
                            SEARCH__searchBoxPlaceholder__translate,
                            [$dictionary.l2.name],
                        )}
                        use:initSearchInput
                        on:input={debounce(350, dictionarySearchInputChange)}
                        bind:this={searchInput}
                        on:keydown={handleKeydownOnMainInput}
                    />
                    <div class="flex flex-col w-10">
                        {#if $searchPhrase.length > 0}
                            <button
                                class="icon text-slate-500 focus:outline-none font-bold flex items-center justify-center text-lg w-10 h-10"
                                data-element="clear"
                                type="button"
                                aria-label="clear"
                                on:click={clearInput}
                                ><svg
                                    height="20"
                                    width="20"
                                    viewBox="0 0 24 24"
                                    fill="currentColor"
                                    class="fillCurrentColor"
                                    ><path
                                        d="M6.325 19.85 4.2 17.675 9.85 12 4.2 6.3l2.125-2.175L12 9.825l5.675-5.7L19.8 6.3 14.15 12l5.65 5.675-2.125 2.175L12 14.15Z"
                                    /></svg
                                ></button
                            >

                            <button
                                class="icon text-slate-500 focus:outline-none font-bold flex items-center justify-center text-2xl w-10 h-10"
                                data-element="clear"
                                type="submit"
                                aria-label="submit"
                            >
                                <span class="icon-magnify" />
                            </button>
                        {/if}
                    </div>
                </form>
            </div>
            <div class="h-full flex-1 overflow-auto">
                <div><Keyboard input={searchInput} inline={true} /></div>

                {#if !$searchPhrase || $searchPhrase.trim().length < 1}
                    <div
                        class="w-full h-full text-lg text-center px-4"
                        style="margin: 10vh 0"
                    >
                        {getMessage(SEARCH__emptyState__Start_typing, [
                            $dictionary.l2.name,
                        ])}
                    </div>
                {:else}
                    <div class="py-1 mt-2">
                        {#if $similarPhrases.straight.afterCount > 0 || $similarPhrases.straight.afterCount > 0}
                            <SimilarPhrasesList
                                activeItem={($similarPhrases.straight
                                    ?.currentPosition || 0) - 1}
                                reverse={false}
                            />
                            <SimilarPhrasesList
                                activeItem={($similarPhrases.reverse
                                    ?.currentPosition || 0) - 1}
                                reverse={true}
                            />
                        {/if}

                        <FullScreenSearchAutomaticTranslations />
                    </div>
                {/if}
            </div>
        </div>
    </MobileScreen>
</LazyAppearingComponent>

<style>
    .changeLanguageButton {
        width: calc(50% - 1.5rem);
    }
    :global(.z-60) {
        z-index: 60;
    }
</style>
